'use client';

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NextIntlClientProvider } from 'next-intl';
import { AppDispatch } from '@/app/store';
import { selectCurrentLanguage, selectIsLanguageInitialized } from '@/app/store/slices/languageSlice';
import { initializeLanguage } from '../utils/initializeLanguage';
import { useAuth } from '../../../hooks/useAuth';

// Import all messages statically (BCP 47 format)
import enUSMessages from '@/locales/en-US.json';
import zhCNMessages from '@/locales/zh-CN.json';
import zhTWMessages from '@/locales/zh-TW.json';
import jaJPMessages from '@/locales/ja-JP.json';
import esESMessages from '@/locales/es-ES.json';
import frFRMessages from '@/locales/fr-FR.json';

// Map locale codes to their message objects
const messagesMap: Record<string, any> = {
  'en-US': enUSMessages,
  'zh-CN': zhCNMessages,
  'zh-TW': zhTWMessages,
  'ja-JP': jaJPMessages,
  'es-ES': esESMessages,
  'fr-FR': frFRMessages,
};

/**
 * LanguageProvider component
 * Initializes language preference on app load
 * Handles both authenticated and guest users
 * Provides NextIntl context for translations
 */
const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch: AppDispatch = useDispatch();
  const isLanguageInitialized = useSelector(selectIsLanguageInitialized);
  const currentLanguage = useSelector(selectCurrentLanguage);
  const { isAuthenticated, isReady: isAuthReady, user } = useAuth();
  const [messages, setMessages] = useState<any>(messagesMap[currentLanguage] || messagesMap['en-US']);

  // Track whether we've already fetched the *authenticated* user's preference
  // so we don't re-fetch on every re-render.
  const [didFetchForUser, setDidFetchForUser] = useState(false);

  // Fallback: if auth hasn't settled within 2 seconds, initialize language
  // with guest defaults (localStorage → browser detection → en-US) so the
  // landing page always renders. Authenticated users who load slower will
  // still get their preference once auth completes — the effect below
  // re-initializes when isAuthReady flips to true.
  useEffect(() => {
    if (isLanguageInitialized || isAuthReady) return;
    const timer = setTimeout(() => {
      initializeLanguage(dispatch, undefined, false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [dispatch, isLanguageInitialized, isAuthReady]);

  useEffect(() => {
    // Wait until auth has settled (hydrated from storage / token validated)
    // before deciding guest vs authenticated init. This prevents the flash
    // where guest/browser language appears before the user preference loads.
    if (!isAuthReady) return;

    // Authenticated user — fetch preference from backend (once).
    if (isAuthenticated && user?.id && !didFetchForUser) {
      setDidFetchForUser(true);
      initializeLanguage(dispatch, parseInt(user.id), true);
      return;
    }

    // Guest user — use localStorage / browser detection (once).
    if (!isAuthenticated && !isLanguageInitialized) {
      initializeLanguage(dispatch, undefined, false);
    }
  }, [dispatch, isAuthReady, isLanguageInitialized, isAuthenticated, user?.id, didFetchForUser]);

  // Load messages when language changes
  useEffect(() => {
    const loadedMessages = messagesMap[currentLanguage] || messagesMap['en-US'];
    setMessages(loadedMessages);
  }, [currentLanguage]);

  // Always provide the context, even with empty messages during initial load
  // This prevents "context not found" errors
  // No key prop - let NextIntlClientProvider handle language changes internally
  return (
    <NextIntlClientProvider
      locale={currentLanguage}
      messages={messages || {}}
      timeZone="UTC"
    >
      {children}
    </NextIntlClientProvider>
  );
};

export default LanguageProvider;
